import type { EChartsOption } from 'echarts';
import React from 'react';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

export interface DualLineChartRow {
  date: string;
  /** Member return since the window start, in percent. */
  port: number;
  /** Benchmark return since the window start, in percent. */
  sp: number;
}

export type DualLineChartProps = {
  /** Both series re-based to percent change from the window's first row (one shared baseline + axis). */
  rows?: DualLineChartRow[];
  memberLabel?: string;
  width?: string;
  height?: string;
};

function linePoints(rows: DualLineChartRow[] | undefined, valueKey: 'port' | 'sp') {
  return rows?.map(row => ({ name: row.date, value: [row.date, row[valueKey]] as [string, number] }));
}

const percentAxis = {
  axisLabel: { formatter: (value: number) => `${value.toFixed(0)}%` },
  axisTick: { show: false },
  scale: true,
  splitLine: { show: false },
  type: 'value' as const,
};

export const DualLineChart = ({ rows, memberLabel, width = '300px', height = '120px' }: DualLineChartProps) => {
  const portfolioSeries = linePoints(rows, 'port');
  const spSeries = linePoints(rows, 'sp');

  const option = React.useMemo(
    () =>
      ({
        grid: { bottom: '22%', height: 'auto', left: 0, right: 0, top: '1%', width: 'auto' },
        series: [
          { color: '#4f86f2', data: portfolioSeries, name: memberLabel, showSymbol: false, smooth: true, type: 'line' },
          { color: '#f367bb', data: spSeries, name: 'S&P 500', showSymbol: false, smooth: true, type: 'line' },
        ],
        tooltip: {
          axisPointer: { animation: false, type: 'none' },
          trigger: 'axis',
          valueFormatter: (value: unknown) => {
            const raw = Array.isArray(value) ? (value.length >= 2 ? value[1] : value[0]) : value;
            const n = Number(raw);
            return Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` : '';
          },
        },
        xAxis: { interval: 0, show: false, splitLine: { show: false }, type: 'time' },
        yAxis: percentAxis,
      }) satisfies EChartsOption,
    [memberLabel, portfolioSeries, spSeries],
  );

  return <ReactECharts notMerge={true} option={option} style={{ height, width }} />;
};
